<template>
  <atlas-article-top
    ref="articleTop"
    :variant="variant"
    :image="imageProps"
    :title="props.article?.title"
    :lead="props.article?.intro"
    :tag="tag"
    :byline="byline"
    :assets="bylineMediaAsset"
    :common-details="commonDetails"
  >
    <!-- VIDEO / EMBED / GALLERY SLOT -->
    <template
      v-if="isMediaAsset"
      #leadasset
    >
      <client-only v-if="leadAsset?.type === 'video'">
        <dn-video
          :video-id="leadAsset.videoId"
          :site-name="leadAsset.siteName"
          :auto-play="leadAsset.autoPlay"
        />
      </client-only>

      <client-only v-else-if="leadAsset?.type === 'embed'">
        <dn-embed v-bind="leadAsset" />
      </client-only>

      <client-only v-else-if="leadAsset?.type === 'picture_gallery'">
        <dn-gallery
          :items="leadAsset.items"
        />
      </client-only>
    </template>

    <!-- BYLINE ACTION SLOT -->
    <template #byline-action>
      <template
        v-for="(item, index) in bylineActionItems"
        :key="index"
      >
        <template v-if="item.showAuthorAlertSubscription">
          <dn-be-notified
            v-if="rtConfPub.alerts"
            :is-tag-view="isOpinionArticle"
            :text="t('Follow me')"
          >
            <dn-alert-subscription
              id="alert-authors"
              :title="t('Choose author')"
              :description="t('You create an alert for all articles written by:')"
              type="authors"
              :items="[{ name: item.name, label: item.name, term: item.username }]"
            />
          </dn-be-notified>
        </template>
      </template>

      <client-only>
        <dn-give-away-button
          v-if="showGiftArticleOption && !isOpinionArticle"
        >
          <atlas-button
            v-if="hasSharingAccess"
            class="secondary"
            :title="t('Gift')"
            icon="common/gift"
            @click="emit('open-share-modal')"
          />
        </dn-give-away-button>
      </client-only>
    </template>
  </atlas-article-top>
</template>

<script setup lang="ts">
import { AtlasArticleTop, AtlasButton } from '@nhst/ui';
import debounce from 'lodash.debounce';
import { type PublicationContext } from '~/types';

const { t } = useI18n();
const { bylineItems, bylineMedia, bylineActionItems, bylineCommonDetailsItems, isOpinionArticle } = useArticle();
const rtConfPub = useRuntimeConfig().public as unknown as PublicationContext;

const props = defineProps({
  article: {
    type: Object,
    required: true
  },
  breadcrumb: {
    type: Object as PropType<{ text: string; path: string }>,
    required: false,
    default: () => null
  },
  showGiftArticleOption: {
    type: Boolean,
    default: false
  },
  hasSharingAccess: {
    type: Boolean,
    default: false
  },
  formats: {
    type: Array as PropType<string[]>,
    required: false,
    default: () => ['webp', 'jpeg']
  }
});

const leadAsset = computed(() => {
  return props.article?.leadAsset ?? null;
});
const isMediaAsset = computed(() => {
  return (
    leadAsset.value?.type === 'video' ||
    leadAsset.value?.type === 'embed' ||
    leadAsset.value?.type === 'picture_gallery'
  );
});

const imageProperty = leadAsset.value?.type === 'picture'
  ? {
      src: leadAsset.value.src,
      ratio: [leadAsset.value.ratio || rtConfPub.article?.top?.imageRatio || '3x2'],
      imageCrops: leadAsset.value.imageCrops,
      pointOfInterest: leadAsset.value.pointOfInterest,
      originalWidth: leadAsset.value.originalWidth,
      originalHeight: leadAsset.value.originalHeight
    }
  : {};

const articleTop = ref<any>(null);
const picture = ref<HTMLElement | null>(null);
const fp = imageProperty.pointOfInterest?.centerX && imageProperty.pointOfInterest?.centerY
  ? [imageProperty.pointOfInterest.centerX, imageProperty.pointOfInterest.centerY]
  : [];
const imageLib = useImage(imageProperty.src, {
  fp,
  poiData: imageProperty.pointOfInterest,
  imageCrops: imageProperty.imageCrops,
  ratio: imageProperty.ratio || '3x2',
  originalWidth: imageProperty.originalWidth,
  originalHeight: imageProperty.originalHeight
});
const pictureWidth = ref(980);
const pictureHeight = ref(imageLib.getHeightByRatio(pictureWidth.value));
const showImage = ref<boolean>(false);
const debouncedHandleResize = debounce(handleResize, 500);
const imageSrc = computed(() => {
  return imageLib.getImageSrc(rtConfPub.imageFormat || 'auto', pictureWidth.value, pictureHeight.value);
});

const bylineMediaAsset = computed(() => {
  if (!isOpinionArticle.value || !bylineMedia.value?.src) {
    return undefined;
  }
  const lib = useImage(bylineMedia.value.src, { ratio: ['1x1'] });
  const width = 200;
  const height = lib.getHeightByRatio(width);
  const format = props.formats?.[0] ?? 'auto';
  const src = lib.getImageSrc(format, width, height);
  if (!src) {
    return undefined;
  }
  return {
    src,
    fetchpriority: 'high',
    loading: 'eager',
    decoding: 'async',
    alt: bylineMedia.value?.alt ?? ''
  };
});

const tag = computed(() => ({
  title: props.breadcrumb?.text,
  to: props.breadcrumb?.path
}));

const byline = computed(() => ({
  items: bylineItems.value,
  separatorWord: t('and')
}));

const commonDetails = computed(() => {
  return [
    bylineCommonDetailsItems.value?.commonDetails,
    rtConfPub.showDatelineInByline ? bylineCommonDetailsItems.value?.dateline : null
  ].filter(Boolean);
});

function handleResize () {
  const pixelRatio = window.devicePixelRatio || 1;
  picture.value = articleTop.value?.pictureRef;
  pictureWidth.value = (picture.value?.offsetWidth || picture.value?.clientWidth || window.innerWidth) * pixelRatio;
  pictureHeight.value = (picture.value?.offsetHeight || picture.value?.clientHeight || window.innerHeight) * pixelRatio;
  if (!showImage.value) {
    showImage.value = true;
  }
}

onMounted(() => {
  if ('ResizeObserver' in window) {
    useResizeObserver(picture, debouncedHandleResize);
  } else {
    // Fallback: use window resize for older browsers
    useEventListener('resize', debouncedHandleResize);
  }

  nextTick(() => {
    requestAnimationFrame(handleResize);
  });
});

// IMAGE MAPPER
const imageProps = computed(() => {
  if (leadAsset.value?.type !== 'picture') {
    return null;
  }

  return {
    class: `figure-${imageLib.getRatio()}`,
    src: showImage.value ? imageSrc.value : undefined,
    alt: leadAsset.value.caption || props.article.title,
    caption: leadAsset.value.caption,
    credit: leadAsset.value.credit,
    ratio: imageProperty?.ratio || ['3x2'],
    loading: 'eager',
    fetchpriority: 'high',
    decoding: 'async'
  };
});

const variant = computed(() => {
  const publicationName = rtConfPub.name;
  const layout = props.article?.dataLayout ?? '';
  const headFont = props.article?.headlineFont ?? '';

  if (isOpinionArticle.value) {
    return [
      publicationName,
      headFont,
      'opinion'
    ].filter(Boolean).join(' ');
  } else if (layout === 'full') {
    return [
      publicationName,
      headFont,
      'fullwidth'
    ].filter(Boolean).join(' ');
  } else {
    return [
      publicationName,
      headFont,
      layout || 'text-first'
    ].filter(Boolean).join(' ');
  }
});

const emit = defineEmits(['open-share-modal']);
</script>

<style lang="scss">

.atlas-article {
  &-top {
    --atlas-article-top_z-index: 10;

    &:has(.dn-gallery.scrolly) {
  --atlas-article-top_max-width: none;
  --atlas-article-top_image_max-width: none;
  --atlas-article-top_image_place-self: stretch;
  --atlas-article-top_padding: 0;
  --atlas-image_figcaption_text-align: center;

  > .image {
    overflow: visible;
    aspect-ratio: auto;
    height: auto;
    max-width: none;
    padding: 0;
    margin: 0;
    contain: none;
  }

   .figcaption {
    display: inline;
    text-align: center;
  }
}
  }

  .dn-byline {
    &.opinion {
      --_dn-byline_be-notified-button_border_color: var(--dn-byline_be-notified-button_border_color, var(--color-teaser-opinion-divider));
      --_dn-byline_be-notified-button_icon_color: var(--dn-byline_be-notified-button_icon_color, var(--color-teaser-opinion-highlight-fg));
      --_dn-byline_be-notified-button__hover: var(--dn-byline-be-notified-button__hover, var(--color-button-secondary-inverted-hover-bg));

      .dn-be-notified {
        .dn-icon {
          width: min-content;
          max-width: min-content;
          stroke: var(--_dn-byline_be-notified-button_icon_color);
        }

        .dn-button {
          &.outline {
            color: var(--_dn-byline_color);
            border-color: var(--_dn-byline_be-notified-button_border_color);

            &:hover {
              background-color: var(--_dn-byline_be-notified-button__hover);

              .dn-icon {
                stroke: var(--_dn-byline_be-notified-button_icon_color);
              }
            }
          }
        }
      }
    }
  }
}

.dn-ma-frame-top {
  &:has(+ .fullwidth, + .wide, + .side-to-side) {
    --dn-ma-frame_width: var(--page-width);
  }

  &:has(+ .fullwidth) {
    margin-bottom: var(--atlas_gap);
  }
}
</style>
